<template>
<div class="px-1 pt-1">
  <!-- Delete Confirmation Modal -->
  <div class="modal fade" id="deleteLabelPrinterModal" ref="deleteLabelPrinterModal" tabindex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="true">
    <div class="modal-dialog" role="document">
      <div class="modal-content" v-if="labelPrinterToDelete">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('page.labelPrinter.deleteConfirmTitle') }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>{{ $t('page.labelPrinter.deleteConfirmMessage', { code: labelPrinterToDelete.code }) }}</p>
          <div class="card">
            <div class="card-body">
              <h6 class="card-title">{{ labelPrinterToDelete.code }}</h6>
              <p class="card-text text-muted mb-0">{{ labelPrinterToDelete.address }}</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-danger" @click="confirmDeleteLabelPrinter" :disabled="isDeleting">
            <div v-if="isDeleting" class="spinner-border spinner-border-sm me-2"></div>
            <font-awesome-icon v-else :icon="['fas', 'trash']" class="me-2" />
            {{ $t('action.delete') }}
          </button>
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" :disabled="isDeleting">
            <font-awesome-icon :icon="['fas', 'xmark']" class="me-2" />
            {{ $t('action.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="input-group pb-3">
    <button class="btn btn-outline-secondary" @click="refresh()" :disabled="isLoading">
      <div v-if="isLoading" class="spinner-border spinner-border-sm"></div>
      <font-awesome-icon v-else :icon="['fas', 'rotate']" />
      <span class="d-none d-sm-inline">{{ $t('action.refresh') }}</span>
    </button>
    <RouterLink :to="{ name: 'label-printer-create'}" class="btn btn-outline-secondary">
      <font-awesome-icon :icon="['fas', 'plus']" />
    </RouterLink>
  </div>

  <div v-if="error" class="alert alert-danger">
    {{ error }}
  </div>
  <div v-else-if="labelPrinters" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
    <table v-if="labelPrinters.length > 0" class="table table-hover table-sm table-responsive-lg table-fit align-middle">
      <thead>
      <tr>
        <th></th>
        <th class="mel-min-w-160">{{ $t('field.generic.code') }}</th>
        <th class="mel-min-w-200">{{ $t('page.labelPrinter.table.address') }}</th>
        <th></th>
      </tr>
      </thead>
      <tbody id="scrollable-content">
      <tr v-for="labelPrinter in labelPrinters" :key="labelPrinter.id">
        <td>
          <RouterLink :to="{ name: 'label-printer', params: { printerId: labelPrinter.id }}" class="btn btn-outline-secondary btn-sm">
            <font-awesome-icon :icon="['fas', 'pen']" />
          </RouterLink>
        </td>
        <td>{{labelPrinter.code}}</td>
        <td>{{labelPrinter.address}}</td>
        <td>
          <button class="btn btn-outline-danger" type="button" @click="showDeleteConfirmation(labelPrinter)">
            <font-awesome-icon :icon="['fas', 'trash']" />
          </button>
        </td>
      </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
  <div v-else class="loading">
    {{ $t('status.loadingEllipsis') }}
  </div>
</div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {Modal} from "bootstrap";
import {storeToRefs} from 'pinia';
import {useLabelPrintersStore} from '@/stores';
import {toast, type ToastOptions} from "vue3-toastify";
import {useI18n} from "vue-i18n";
import LabelPrinter from "@/models/frontend/LabelPrinter";

const i18n = useI18n();

const labelPrinterStore = useLabelPrintersStore();
const { error: labelPrinterStoreError } = storeToRefs(labelPrinterStore);
const labelPrinters = ref<LabelPrinter[]>([]);
const isLoading = ref(false);
const error = ref("");
const offsetTop = ref(0);

const deleteLabelPrinterModal = ref<HTMLElement | null>(null);
const labelPrinterToDelete = ref<LabelPrinter | null>(null);
const isDeleting = ref(false);

onMounted(async () => {
  await refresh();
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const showDeleteConfirmation = (labelPrinter: LabelPrinter) => {
  labelPrinterToDelete.value = labelPrinter;
  if (deleteLabelPrinterModal.value) {
    const modal = new Modal(deleteLabelPrinterModal.value);
    modal.show();
  }
};

const hideDeleteModal = () => {
  if (deleteLabelPrinterModal.value) {
    const modal = Modal.getInstance(deleteLabelPrinterModal.value);
    modal?.hide();
  }
  labelPrinterToDelete.value = null;
};

const confirmDeleteLabelPrinter = async () => {
  if (!labelPrinterToDelete.value) return;

  const code = labelPrinterToDelete.value.code;
  try {
    isDeleting.value = true;
    await labelPrinterStore.delete(labelPrinterToDelete.value.id);

    if (labelPrinterStoreError.value) {
      toast.error(i18n.t('page.labelPrinter.deleteError', { error: labelPrinterStoreError.value }), { theme: 'colored', autoClose: 2000 } as ToastOptions);
    } else {
      toast.success(i18n.t('page.labelPrinter.deletedSuccessfully', { code }), { autoClose: 3000 });
    }

    hideDeleteModal();
    await refresh();
  } finally {
    isDeleting.value = false;
  }
};

async function refresh() {
  isLoading.value = true;
  error.value = "";

  try {
    labelPrinters.value = await labelPrinterStore.getAll();
  }
  finally {
    error.value = labelPrinterStoreError.value;
    isLoading.value = false;
  }
}

</script>

<style scoped>
.mel-min-w-160 {
  min-width: 160px;
}
.mel-min-w-200 {
  min-width: 200px;
}
</style>
